import React, { useState, useContext, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { FaFolder } from "react-icons/fa";
import { CgHome } from "react-icons/cg";
import * as BsIcons from "react-icons/bs";
import * as TbIcons from "react-icons/tb";
import * as MdIcons from "react-icons/md";
import * as HiIcons from "react-icons/hi2";
import * as RiIcons from "react-icons/ri";
import * as PiIcons from "react-icons/pi";
import * as Io5Icons from "react-icons/io5";
import * as Vsc from "react-icons/vsc";
import * as F6Icons from "react-icons/fa6";
import * as GrIcons from "react-icons/gr";
import "./Dock.css";
import VerticalDivider from "../VerticalDivider/VerticalDivider";
import { GeneralContext } from "../../GeneralProvider";
import { useLanguage } from "../../LanguageProvider";

const MENU_WIDTH = 180;
const MENU_HEIGHT = 120;
const MENU_PADDING = 8;

const Dock = () => {
  const {
    selectedProjectID,
    openedWindows,
    setOpenedWindows,
    handleUpdateOpenedWindows,
  } = useContext(GeneralContext);

  const { getTranslation } = useLanguage();
  const [active, setActive] = useState("home");
  const dockRef = useRef(null);
  const [overlay, setOverlay] = useState(false);
  const [peek, setPeek] = useState(false);
  const [isDockHover, setIsDockHover] = useState(false);
  const stickyRef = useRef(false);

  const getFsCount = () => Number(document.body.dataset.mwFsCount || 0);

  const resetFullscreenUI = useCallback(() => {
    document.body.classList.remove("mw-fullscreen");
    window.dispatchEvent(new Event("mw:exit-fullscreen"));
    setOverlay(false);
    setPeek(false);
    stickyRef.current = false;
    setIsDockHover(false);
  }, []);

  useEffect(() => {
    const onExit = () => resetFullscreenUI();
    window.addEventListener("mw:exit-fullscreen", onExit);
    return () => window.removeEventListener("mw:exit-fullscreen", onExit);
  }, [resetFullscreenUI]);

  useEffect(() => {
    const onFsChange = (e) => {
      const count = e?.detail?.count ?? getFsCount();
      const safeCount = Math.max(0, count);
      if (safeCount === 0) {
        setOverlay(false);
        setPeek(false);
        stickyRef.current = false;
        setIsDockHover(false);
      } else {
        // En az bir FS varsa overlay davranışı aktif kalabilir
        setOverlay(true);
      }
    };
    window.addEventListener("mw:fullscreen-change", onFsChange);
    // İlk açılış durumu
    onFsChange();
    return () => window.removeEventListener("mw:fullscreen-change", onFsChange);
  }, []);

  useEffect(() => {
    const onMove = (e) => {
      const fs = document.body.classList.contains("mw-fullscreen");
      setOverlay(fs);
      if (!fs) {
        setPeek(false);
        stickyRef.current = false;
        return;
      }
      // Dock yüksekliğini güvenli bölgeye dahil et
      const dockEl = dockRef.current;
      const dockH = dockEl ? (dockEl.getBoundingClientRect().height || 0) : 0;
      const margin = 6;
      const threshold = window.innerHeight - Math.max(dockH, 64) + margin; // en az 64px güvenli

      // Eğer zaten dock üzerindeysek ya da sticky ise açık kalsın
      if (isDockHover || stickyRef.current) {
        setPeek(true);
        return;
      }
      // Alt sınıra yaklaşınca aç
      setPeek(e.clientY >= threshold);
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, [isDockHover]);

  const [contextMenu, setContextMenu] = useState({
    visible: false,
    x: 0,
    y: 0,
    item: null,
  });

  useEffect(() => {
    const handleDown = () => {
      if (contextMenu.visible) {
        setContextMenu((cm) => ({ ...cm, visible: false }));
      }
    };
    document.addEventListener("mousedown", handleDown);
    return () => document.removeEventListener("mousedown", handleDown);
  }, [contextMenu.visible]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        setContextMenu((cm) => ({ ...cm, visible: false }));
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const filteredMenuItems = openedWindows.filter(
    (item) => item.projectId === selectedProjectID || item.independent
  );

  const getIconComponent = (iconName) => {
    const Icon =
      TbIcons[iconName] ||
      BsIcons[iconName] ||
      MdIcons[iconName] ||
      HiIcons[iconName] ||
      RiIcons[iconName] ||
      PiIcons[iconName] ||
      Io5Icons[iconName] ||
      Vsc[iconName] ||
      F6Icons[iconName] ||
      GrIcons[iconName];
    return Icon ? <Icon size={24} /> : <FaFolder size={24} />;
  };

  const handleItemClick = (item) => {
    if (item === "home") {
      resetFullscreenUI();
      const allInactive = filteredMenuItems.every(
        (menuItem) => menuItem.isActive === false
      );
      const updatedMenuItems = filteredMenuItems.map((menuItem) => ({
        ...menuItem,
        isActive: allInactive ? true : false,
        isSelected: false,
      }));
      setOpenedWindows(updatedMenuItems);
      handleUpdateOpenedWindows(updatedMenuItems);
      setActive("home");
    } else {
      setActive(item.name);

      // 🔹 En yüksek z-index'i artır ve window'a ata
      const newHighest = window.__highestZIndex
        ? ++window.__highestZIndex
        : (window.__highestZIndex = 1000);

      const updatedMenuItems = openedWindows.map((menuItem) => {
        if (menuItem.id === item.id) {
          return {
            ...menuItem,
            isActive: true,
            isSelected: true,
            zIndex: newHighest // pencere en öne gelsin
          };
        }
        return { ...menuItem, isSelected: false };
      });

      setOpenedWindows(updatedMenuItems);
      handleUpdateOpenedWindows(updatedMenuItems);

      // 🔹 ModuleWindow'lara da z-index güncellemesini duyur
      window.dispatchEvent(new CustomEvent("mw:zindex-change", { detail: { id: item.id, zIndex: newHighest } }));
    }

    if (contextMenu.visible) {
      setContextMenu((cm) => ({ ...cm, visible: false }));
    }
  };

  const handleContextMenu = useCallback((e, item) => {
    e.preventDefault();
    if (!item || item.name === "home") return;

    let x = e.clientX;
    let y = e.clientY;

    if (x + MENU_WIDTH + MENU_PADDING > window.innerWidth) {
      x = window.innerWidth - MENU_WIDTH - MENU_PADDING;
    }
    if (y + MENU_HEIGHT + MENU_PADDING > window.innerHeight) {
      y = window.innerHeight - MENU_HEIGHT - MENU_PADDING;
    }
    if (x < MENU_PADDING) x = MENU_PADDING;
    if (y < MENU_PADDING) y = MENU_PADDING;

    setContextMenu({
      visible: true,
      x,
      y,
      item,
    });
  }, []);

  const handleMinimize = () => {
    if (contextMenu.item) {
      const nextWindows = openedWindows.map((win) =>
        win.id === contextMenu.item.id
          ? { ...win, isActive: false, isSelected: false }
          : win
      );
      // Minimize sonrası ekranda hâlâ fullscreen var mı?
      const fsLeft = nextWindows.some((w) => w.isFullScreen);
      if (!fsLeft) {
        resetFullscreenUI();
      } else {
        // Dock peek/overlay açık kalsın ve sayacı senkron tutalım
        const count = nextWindows.filter((w) => w.isFullScreen).length;
        setOverlay(true);
        window.dispatchEvent(new CustomEvent("mw:fullscreen-change", { detail: { count } }));
      }
      setOpenedWindows(nextWindows);
      handleUpdateOpenedWindows(nextWindows);
    }
    setContextMenu((cm) => ({ ...cm, visible: false }));
  };

  const handleClose = () => {
    if (contextMenu.item) {
      const nextWindows = openedWindows.filter(
        (win) => win.id !== contextMenu.item.id
      );
      // Kapanıştan sonra fullscreen kalan var mı?
      const fsLeft = nextWindows.some((w) => w.isFullScreen);
      if (!fsLeft) {
        // Artık hiç FS yok → peek/overlay’i kapat
        resetFullscreenUI();
      } else {
        // Hâlâ FS var → peek/overlay açık kalsın ve yeni sayacı yayınla
        const count = nextWindows.filter((w) => w.isFullScreen).length;
        setOverlay(true);
        window.dispatchEvent(new CustomEvent("mw:fullscreen-change", { detail: { count } }));
      }
      setOpenedWindows(nextWindows);
      handleUpdateOpenedWindows(nextWindows);
    }
    setContextMenu((cm) => ({ ...cm, visible: false }));
  };

  const handleReposition = () => {
    if (contextMenu.item) {
      const desiredWidth = 1300;
      const desiredHeight = 750;
      const centerX = Math.max(0, (window.innerWidth - desiredWidth) / 2);
      const centerY = Math.max(0, (window.innerHeight - desiredHeight) / 2);

      const updatedWindows = openedWindows.map((win) => {
        if (win.id === contextMenu.item.id) {
          return {
            ...win,
            isActive: true,
            isSelected: true,
            size: { width: desiredWidth, height: desiredHeight },
            position: { x: centerX, y: centerY },
          };
        }
        return { ...win, isSelected: false };
      });

      setOpenedWindows(updatedWindows);
      handleUpdateOpenedWindows(updatedWindows);
    }
    setContextMenu((cm) => ({ ...cm, visible: false }));
  };

  const handleEnter = () => {
    setIsDockHover(true);
    stickyRef.current = true;
    setPeek(true);
  };
  const handleLeave = () => {
    setIsDockHover(false);
    // Küçük bir gecikme ile latch’i bırak (flicker engel)
    setTimeout(() => { stickyRef.current = false; }, 120);
  };

  return (
    <div className={`dock-fixed-wrap ${overlay ? "dock--overlay" : ""} ${peek ? "peek-over-fullscreen" : ""}`}>
      <div
        className="dock-container"
        ref={dockRef}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
      >
        <div
          key={"home"}
          className={`dock-item ${active === "home" ? "active" : ""}`}
          onClick={() => handleItemClick("home")}
          onContextMenu={(e) => e.preventDefault()}
        >
          <div className={`icon-frame ${active === "home" ? "active" : ""}`}>
            <CgHome />
          </div>
        </div>

        {filteredMenuItems.length > 0 && (
          <VerticalDivider style={{ alignSelf: "center" }} />
        )}

        {filteredMenuItems.map((item) => (
          <div
            key={item.id}
            className={`dock-item ${item.isSelected ? "active" : ""}`}
            onClick={() => handleItemClick(item)}
            onContextMenu={(e) => handleContextMenu(e, item)}
            title={item.name}
          >
            <div className={`icon-frame ${item.isSelected ? "active" : ""}`}>
              {getIconComponent(item.icon)}
            </div>
            {item.isSelected && <div className="active-dot"></div>}
          </div>
        ))}
      </div>

      {contextMenu.visible &&
        createPortal(
          <div
            className="context-menu"
            style={{ top: `${contextMenu.y}px`, left: `${contextMenu.x}px` }}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <button onClick={handleMinimize}>{getTranslation("minimize")}</button>
            <button onClick={handleReposition}>{getTranslation("reposition")}</button>
            <button onClick={handleClose}>{getTranslation("close")}</button>
          </div>,
          document.body
        )}
    </div>
  );
};

export default Dock;